/**
 * THE AUTHORITY for what a URL may carry to Vercel Web Analytics (WR-651; moved here by WR-660).
 *
 * THE DEFECT IT CLOSES. A sign-in email opens `/auth/confirm?token_hash=…`; the page view is reported
 * as the page LOADS, before the person presses GO, so a still-valid one-time sign-in code left for the
 * analytics provider. Measured on the wire (wr651-s1 Stage 3) through TWO channels:
 *   1. the beacon BODY — the served collector reports `location.href` verbatim unless a `beforeSend`
 *      hook rewrites it. `analyticsBeforeSend` below closes this.
 *   2. the beacon's HTTP `Referer` HEADER — the browser default (`strict-origin-when-cross-origin`)
 *      sends the full URL to our own origin, and no hook can touch a header. `REFERRER_POLICY` below,
 *      set as a document `<meta name="referrer">` in index.html, closes this.
 *
 * THE RULE: strip EVERYTHING from the first `?` or `#`. Not a blocklist — two secret-bearing
 * parameters existed when this was written (`token_hash`, Stripe's `session_id`) and a list keyed on
 * one would have missed the other. Nothing the product reads from analytics lives in a query (CTO
 * ruling 2026-09-25: UTM reporting is not on our plan). Page views are REWRITTEN, never dropped —
 * DATA counts `/auth/confirm` loads as "opened a sign-in link". The one exception is a browser that has
 * opted out with `?notrack=1` (WR-1679, on `analyticsBeforeSend` below).
 *
 * AUTHORITY AND MIRROR (CTO seat ruling 58-24, 2026-09-26). The docs site deploys from the
 * `docs-site` git branch, which shares no branch with the app (PM ruling 2026-09-25: no docs site on
 * develop). So the rule exists twice: THIS file is the authority; the docs branch's
 * `src/shared/analyticsUrlPrivacy.ts` (under the site root) is its MIRROR. `npm run docs:audit` (check
 * D-b) fails when their CODE differs, token for token; comments and whitespace do not count. It runs
 * in CI's architecture-fitness job and at every release.
 * ⛔ ORDER OF CHANGE: update the MIRROR first (vault → docs branch), then this file. The other order
 * reds D-b on your PR, by design — that red is the dual-write gate working.
 *
 * ⛔ ZERO IMPORTS, BY CONTRACT: it crosses the website-entry boundary dragging nothing
 * (websiteEntryExcludesCadEngine.test.ts), and it must stay token-comparable with the mirror, which
 * the docs site builds alone. Enforced by `analyticsUrlPrivacyWiring.test.ts` C5 (beside this file's
 * behavioural test in `__tests__/`).
 *
 * ⚠ WHAT THIS DOES NOT REACH: the body's `r` field (an inbound cross-host referrer, governed by the
 * LINKING page's policy), URL PATHS (`/admin/user/:userId` carries an id), and the host itself — Vercel
 * serves the page, so its edge sees the full request URL. The accurate claim is "recorded BY ANALYTICS
 * without any query string".
 *
 * @see architecture/Website/wr651-s1/approval-gate.md
 * @see architecture/Website/wr660-s1/approval-gate.md
 */

/**
 * Rewrites the URL of every analytics event: everything from the first `?` or `#` is removed.
 *
 * OPT-OUT (WR-1679). `?notrack=1` on any page sets localStorage `va-disable` and `?notrack=0` clears
 * it; while it is set, every event is DROPPED (`null` cancels the event — @vercel/analytics
 * `BeforeSend`, dist/index.d.ts). Per browser and per site, by design: it travels with the browser.
 * Storage can be blocked (Safari private mode): every access is guarded, nothing throws, and
 * `?notrack=1` still drops the event it arrives on. Events that are sent keep the stripping above,
 * so `notrack` itself never leaves.
 */
export function analyticsBeforeSend<E extends { url: string }>(event: E): E | null {
  let flag: string | null = null;
  try {
    flag = new URL(event.url, "https://stagecraft.com").searchParams.get("notrack");
  } catch {
    // unparseable URL: no flag
  }
  let optedOut = flag === "1";
  try {
    if (flag === "1") localStorage.setItem("va-disable", "1");
    else if (flag === "0") localStorage.removeItem("va-disable");
    else optedOut = localStorage.getItem("va-disable") !== null;
  } catch {
    // storage blocked: only this URL's flag decides
  }
  if (optedOut) return null;
  const cut = event.url.search(/[?#]/);
  return cut === -1 ? event : { ...event, url: event.url.slice(0, cut) };
}

/** The document referrer policy both sites set, so the beacon's `Referer` header is origin-only. */
export const REFERRER_POLICY = "strict-origin";
